// utils/sessionManager.js
export class SessionManager {
  static SESSION_CONFIG = {
    INACTIVITY_TIMEOUT: 2 * 60 * 60 * 1000, // 2 hours
    MAX_SESSION_TIME: 8 * 60 * 60 * 1000,   // 8 hours (matches your JWT)
    WARNING_TIME: 5 * 60 * 1000,            // 5 minutes warning
    CHECK_INTERVAL: 60 * 1000,              // Check every minute
    STORAGE_KEY: 'userSession'
  };


// static SESSION_CONFIG = {
//   INACTIVITY_TIMEOUT: 2 * 60 * 1000, // Change to 2 minutes for testing
//   MAX_SESSION_TIME: 8 * 60 * 60 * 1000,   
//   WARNING_TIME: 30 * 1000,            // Change to 30 seconds for testing
//   CHECK_INTERVAL: 10 * 1000,          // Change to 10 seconds for testing
//   STORAGE_KEY: 'userSession'
// };

  static setUserSession(userData) {
    const now = Date.now();
    const sessionData = {
      user: userData,
      loginTime: now,
      lastActivity: now,
      expiresAt: now + this.SESSION_CONFIG.INACTIVITY_TIMEOUT,
      maxExpiresAt: now + this.SESSION_CONFIG.MAX_SESSION_TIME
    };
    
    localStorage.setItem(this.SESSION_CONFIG.STORAGE_KEY, JSON.stringify(sessionData));
    localStorage.setItem('user', JSON.stringify(userData)); // Keep compatibility
    
    console.log('🔐 Session created:', {
      user: userData.UserName,
      expiresAt: new Date(sessionData.expiresAt),
      maxExpiresAt: new Date(sessionData.maxExpiresAt)
    });
  }

  static getUserSession() {
    try {
      const sessionData = localStorage.getItem(this.SESSION_CONFIG.STORAGE_KEY);
      if (!sessionData) {
        // Check for legacy user data
        const legacyUser = localStorage.getItem('user');
        if (legacyUser) {
          console.log('🔄 Migrating legacy session...');
          const userData = JSON.parse(legacyUser);
          this.setUserSession(userData);
          return this.getUserSession();
        }
        return null;
      }

      const parsed = JSON.parse(sessionData);
      const now = Date.now();

      // Check if session has expired
      if (now > parsed.maxExpiresAt) {
        console.log('❌ Session expired (max time reached)');
        this.clearSession();
        return null;
      }

      if (now > parsed.expiresAt) {
        console.log('❌ Session expired (inactivity timeout)');
        this.clearSession();
        return null;
      }

      return parsed.user;
    } catch (error) {
      console.error('❌ Error reading session:', error);
      this.clearSession();
      return null;
    }
  }

  static updateActivity() {
    try {
      const sessionData = localStorage.getItem(this.SESSION_CONFIG.STORAGE_KEY);
      if (!sessionData) return false;

      const parsed = JSON.parse(sessionData);
      const now = Date.now();

      // Don't extend beyond max session time
      if (now > parsed.maxExpiresAt) {
        this.clearSession();
        return false;
      }

      // Update last activity and extend inactivity timeout
      parsed.lastActivity = now;
      parsed.expiresAt = now + this.SESSION_CONFIG.INACTIVITY_TIMEOUT;

      localStorage.setItem(this.SESSION_CONFIG.STORAGE_KEY, JSON.stringify(parsed));
      return true;
    } catch (error) {
      console.error('❌ Error updating activity:', error);
      return false;
    }
  }

  static getTimeUntilExpiry() {
    try {
      const sessionData = localStorage.getItem(this.SESSION_CONFIG.STORAGE_KEY);
      if (!sessionData) return 0;

      const parsed = JSON.parse(sessionData);
      const now = Date.now();
      
      return Math.max(0, Math.min(
        parsed.expiresAt - now,
        parsed.maxExpiresAt - now
      ));
    } catch (error) {
      return 0;
    }
  }

  static isNearExpiry() {
    const timeLeft = this.getTimeUntilExpiry();
    return timeLeft > 0 && timeLeft <= this.SESSION_CONFIG.WARNING_TIME;
  }

  static clearSession() {
    localStorage.removeItem(this.SESSION_CONFIG.STORAGE_KEY);
    localStorage.removeItem('user');
    localStorage.removeItem('qbConnected');
    localStorage.removeItem('qbAlertDismissed');
    console.log('🔒 Session cleared');
  }

  static extendSession() {
    const sessionData = localStorage.getItem(this.SESSION_CONFIG.STORAGE_KEY);
    if (!sessionData) return false;

    try {
      const parsed = JSON.parse(sessionData);
      const now = Date.now();
      
      // Extend both activity timer and add some buffer to max time
      parsed.lastActivity = now;
      parsed.expiresAt = now + this.SESSION_CONFIG.INACTIVITY_TIMEOUT;
      
      localStorage.setItem(this.SESSION_CONFIG.STORAGE_KEY, JSON.stringify(parsed));
      console.log('⏰ Session extended');
      return true;
    } catch (error) {
      console.error('❌ Error extending session:', error);
      return false;
    }
  }
}

// Hook for activity tracking
export const useActivityTracking = (onSessionExpired) => {
  const updateActivity = () => {
    const updated = SessionManager.updateActivity();
    if (!updated && onSessionExpired) {
      onSessionExpired();
    }
  };

  const startTracking = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  };

  return { startTracking, updateActivity };
};